/**
 * NotificationPrefsCard — patient-facing controls for email + SMS
 * subscriptions, grouped by category (order updates, promotions, etc).
 *
 * Categories flagged as `transactional` on the backend are rendered
 * disabled + always-checked so patients understand they can't opt out
 * of care-critical mail (receipts, prescriber messages, security).
 */
import { useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, Mail, MessageSquare, ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";

export default function NotificationPrefsCard() {
  const [categories, setCategories] = useState([]);
  const [prefs, setPrefs] = useState({ email: {}, sms: {} });
  const [initial, setInitial] = useState({ email: {}, sms: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/notify/prefs");
        setCategories(data.categories || []);
        setPrefs(data.prefs || { email: {}, sms: {} });
        setInitial(JSON.parse(JSON.stringify(data.prefs || { email: {}, sms: {} })));
      } catch (e) {
        toast.error("Couldn't load notification preferences");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(prefs) !== JSON.stringify(initial),
    [prefs, initial]
  );

  const toggle = (channel, key, val) => {
    setPrefs((p) => ({ ...p, [channel]: { ...p[channel], [key]: val } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/notify/prefs", {
        email: prefs.email,
        sms: prefs.sms,
      });
      setPrefs(data.prefs);
      setInitial(JSON.parse(JSON.stringify(data.prefs)));
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const unsubAllMarketing = () => {
    setPrefs((p) => {
      const next = { email: { ...p.email }, sms: { ...p.sms } };
      for (const c of categories) {
        if (!c.transactional) {
          next.email[c.key] = false;
          next.sms[c.key] = false;
        }
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div id="notification-prefs" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 text-sm text-slate-500 dark:text-slate-400">
        Loading notification preferences…
      </div>
    );
  }

  return (
    <div id="notification-prefs" data-testid="notification-prefs-card"
         className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 scroll-mt-24">
      <div className="flex items-start gap-3 mb-4">
        <div className="rounded-xl bg-purple-600/10 p-2">
          <Bell className="w-5 h-5 text-purple-700 dark:text-purple-300" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900 dark:text-white">Notification preferences</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Choose what emails and text messages you get from GLPFlo. Order and account-security messages are required by law and can&rsquo;t be turned off.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={unsubAllMarketing}
          data-testid="unsub-all-marketing-btn"
        >
          Unsubscribe from all marketing
        </Button>
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-12 gap-3 px-2 pb-2 border-b border-slate-100 dark:border-slate-800 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        <div className="col-span-6 md:col-span-7">Category</div>
        <div className="col-span-3 md:col-span-3 flex items-center justify-center gap-1">
          <Mail className="w-3.5 h-3.5" /> Email
        </div>
        <div className="col-span-3 md:col-span-2 flex items-center justify-center gap-1">
          <MessageSquare className="w-3.5 h-3.5" /> Text
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {categories.map((c) => (
          <div key={c.key} className="grid grid-cols-12 gap-3 items-center py-3 px-2" data-testid={`prefs-row-${c.key}`}>
            <div className="col-span-6 md:col-span-7">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-sm text-slate-900 dark:text-white">{c.label}</div>
                {c.transactional && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
                    <ShieldCheck className="w-3 h-3" /> Required
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{c.description}</div>
            </div>
            <div className="col-span-3 md:col-span-3 flex justify-center">
              <Switch
                checked={!!prefs.email?.[c.key]}
                onCheckedChange={(v) => toggle("email", c.key, v)}
                disabled={c.transactional}
                data-testid={`prefs-email-${c.key}`}
              />
            </div>
            <div className="col-span-3 md:col-span-2 flex justify-center">
              <Switch
                checked={!!prefs.sms?.[c.key]}
                onCheckedChange={(v) => toggle("sms", c.key, v)}
                disabled={c.transactional}
                data-testid={`prefs-sms-${c.key}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="save-notification-prefs-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : dirty ? "Save preferences" : "Saved"}
        </Button>
      </div>
    </div>
  );
}
